import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import {
  ActionsEnum,
  addEstimateError,
  addEstimateSuccess,
  approveEstimateError,
  approveEstimateSuccess,
  createJobError,
  createJobSuccess,
  deleteEstimateError,
  deleteEstimateSuccess,
  deleteAttachmentError,
  deleteAttachmentSuccess,
  fetchAttachmentError,
  fetchAttachmentSuccess,
  fetchJobPortalDataError,
  fetchJobPortalDataSuccess,
  fetchJobsError,
  fetchJobsSuccess,
  IAddEstimate,
  IAddEstimateError,
  IAddEstimateSuccess,
  IApproveEstimateError,
  IApproveEstimateSuccess,
  ICreateJob,
  ICreateJobError,
  ICreateJobSuccess,
  IDeleteAttachment,
  IDeleteAttachmentError,
  IDeleteAttachmentSuccess,
  IDeleteEstimate,
  IDeleteEstimateError,
  IDeleteEstimateSuccess,
  IFetchAttachment,
  IFetchAttachmentError,
  IFetchAttachmentSuccess,
  IFetchJobPortalData,
  IFetchJobPortalDataError,
  IFetchJobPortalDataSuccess,
  IFetchJobs,
  IFetchJobsError,
  IFetchJobsSuccess,
  INavigateToEstimateCreation,
  IAddAttachmentError,
  ISaveNoteSuccess,
  saveNoteSuccess,
  ISaveNote,
  IActionError,
  actionError,
  IConvertToInvoice,
} from './job-data.actions';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { JobDataService } from './job-data.service';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { Action } from '@ngrx/store';
import { EstimateDataService } from '../estimate-data/estimate-data.service';
import { EstimateStatusesEnum } from '../estimate-data/estimate-data.data';
import { NavigationExtras } from '@angular/router';
import {
  ADD_CLIENT_QUERY_PARAM,
  ADD_JOB_QUERY_PARAM,
  DOCUMENTS_NEW_ROUTE,
} from '../document-data/document-data.data';
import { ESTIMATES_MASTER_ROUTE } from '../main/main.data';
import { NavigationService } from 'i2g-ng-utils';
import { DocumentDataActionBuilder } from '../document-data/document-data-action.builder';
import {
  copyDocument,
  CopyDocument,
} from '../document-data/document-data.actions';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { v4 } from 'uuid';
import { EntityTypesEnum } from '../../app.enum';

@Injectable()
export class JobDataEffects {
  constructor(
    private _actions: Actions,
    private _jobDataService: JobDataService,
    private _globalBannerService: GlobalBannerService,
    private _estimateDataService: EstimateDataService,
    private _navigationService: NavigationService,
    private _documentDataActionBuilder: DocumentDataActionBuilder,
    private _settingsDataStore: SettingsDataStore
  ) {}

  @Effect()
  fetchJobs(): Observable<IFetchJobsSuccess | IFetchJobsError> {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_JOBS),
      switchMap(({ payload }: IFetchJobs) => {
        return this._jobDataService.fetchJobs(payload.clientId).pipe(
          map((jobs) => fetchJobsSuccess(jobs)),
          catchError((error) => of(fetchJobsError(error)))
        );
      })
    );
  }

  @Effect()
  fetchJobPortalData(): Observable<
    IFetchJobPortalDataSuccess | IFetchJobPortalDataError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_JOB_PORTAL_DATA),
      switchMap(({ payload }: IFetchJobPortalData) => {
        return this._jobDataService.fetchJobPortalData(payload.jobId).pipe(
          map((portalData) => fetchJobPortalDataSuccess(portalData)),
          catchError((error) => of(fetchJobPortalDataError(error)))
        );
      })
    );
  }

  @Effect()
  createJob(): Observable<ICreateJobSuccess | ICreateJobError> {
    return this._actions.pipe(
      ofType(ActionsEnum.CREATE_JOB),
      switchMap(({ payload }: ICreateJob) => {
        return this._jobDataService.createJob(payload.clientId).pipe(
          map((job) => createJobSuccess(job)),
          catchError((error) => of(createJobError(error)))
        );
      })
    );
  }

  @Effect()
  addEstimate(): Observable<IAddEstimateSuccess | IAddEstimateError> {
    return this._actions.pipe(
      ofType(ActionsEnum.ADD_ESTIMATE),
      switchMap(({ payload }: IAddEstimate) => {
        return this._jobDataService
          .addEstimate(payload.documentId, payload.jobId)
          .pipe(
            map((jobEstimate) => addEstimateSuccess(jobEstimate)),
            catchError((error) => of(addEstimateError(error)))
          );
      })
    );
  }

  @Effect()
  approveEstimate(): Observable<
    IApproveEstimateSuccess | IApproveEstimateError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.APPROVE_ESTIMATE),
      switchMap(({ payload }: IAddEstimate) => {
        return this._estimateDataService
          .markEstimateAsDone(payload.documentId, EstimateStatusesEnum.approved)
          .pipe(
            // TODO[job]: backend also has same logic to retrieve approval date (states.estimate_done_date)
            // maybe it's a good idea to have approval endpoint for job
            map((estimate) =>
              approveEstimateSuccess(
                payload.documentId,
                estimate.states.estimate_done_date
              )
            ),
            catchError((error) => of(approveEstimateError(error)))
          );
      })
    );
  }

  @Effect({
    dispatch: false,
  })
  navigateToEstimateCreation() {
    return this._actions.pipe(
      ofType(ActionsEnum.NAVIGATE_TO_ESTIMATE_CREATION),
      tap(({ payload }: INavigateToEstimateCreation) => {
        const navigationExtras: NavigationExtras = {
          queryParams: {
            [ADD_CLIENT_QUERY_PARAM]: payload.clientId,
            [ADD_JOB_QUERY_PARAM]: payload.jobId,
          },
        };

        void this._navigationService.goTo(
          [ESTIMATES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
          navigationExtras
        );
      })
    );
  }

  @Effect()
  deleteEstimate(): Observable<IDeleteEstimateSuccess | IDeleteEstimateError> {
    return this._actions.pipe(
      ofType(ActionsEnum.DELETE_ESTIMATE),
      switchMap(({ payload }: IDeleteEstimate) => {
        const { documentId, jobId } = payload;
        return this._jobDataService.deleteEstimate(documentId, jobId).pipe(
          map((jobEstimate) => deleteEstimateSuccess(jobEstimate)),
          catchError((error) => of(deleteEstimateError(error)))
        );
      })
    );
  }

  @Effect({ dispatch: false })
  errorExists(): Observable<Action> {
    return this._actions.pipe(
      ofType(
        ActionsEnum.FETCH_JOBS_ERROR,
        ActionsEnum.CREATE_JOB_ERROR,
        ActionsEnum.FETCH_JOB_PORTAL_DATA_ERROR,
        ActionsEnum.ADD_ESTIMATE_ERROR,
        ActionsEnum.APPROVE_ESTIMATE_ERROR,
        ActionsEnum.DELETE_ESTIMATE_ERROR,
        ActionsEnum.ADD_ATTACHMENT_ERROR,
        ActionsEnum.ACTION_ERROR
      ),
      tap(
        ({
          payload,
        }:
          | IFetchJobsError
          | ICreateJobError
          | IFetchJobPortalDataError
          | IAddEstimateError
          | IApproveEstimateError
          | IDeleteEstimateError
          | IAddAttachmentError) => {
          this._globalBannerService.showSystemErrorBanner(payload.error);
        }
      )
    );
  }

  @Effect()
  fetchAttachment(): Observable<
    IFetchAttachmentSuccess | IFetchAttachmentError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.FETCH_ATTACHMENT),
      switchMap(({ payload }: IFetchAttachment) => {
        return this._jobDataService
          .fetchAttachment(payload.attachmentId, payload.jobId)
          .pipe(
            map((attachment) => fetchAttachmentSuccess(attachment)),
            catchError((error) => of(fetchAttachmentError(error)))
          );
      })
    );
  }

  @Effect()
  deleteAttachment(): Observable<
    IDeleteAttachmentSuccess | IDeleteAttachmentError
  > {
    return this._actions.pipe(
      ofType(ActionsEnum.DELETE_ATTACHMENT),
      switchMap(({ payload }: IDeleteAttachment) => {
        const { attachmentId, jobId } = payload;
        return this._jobDataService.deleteAttachment(attachmentId, jobId).pipe(
          map((attachment) => deleteAttachmentSuccess(attachment)),
          catchError((error) => of(deleteAttachmentError(error)))
        );
      })
    );
  }

  @Effect()
  saveNote(): Observable<ISaveNoteSuccess | IActionError> {
    return this._actions.pipe(
      ofType(ActionsEnum.SAVE_NOTE),
      switchMap(({ payload }: ISaveNote) => {
        const { content, jobId } = payload;

        return this._jobDataService.saveNote(content, jobId).pipe(
          map((note) => saveNoteSuccess(note)),
          catchError((error) => of(actionError(error)))
        );
      })
    );
  }

  @Effect()
  convertToInvoice(): Observable<CopyDocument | IActionError> {
    return this._actions.pipe(
      ofType(ActionsEnum.CONVERT_TO_INVOICE),
      switchMap(({ payload }: IConvertToInvoice) => {
        const { documentId, jobId, clientId } = payload;

        return this._estimateDataService.getEntity(documentId).pipe(
          withLatestFrom(this._settingsDataStore.getSettings()),
          map(([estimate, settings]) => {
            const copyDocumentOptions = this._documentDataActionBuilder.buildConvertToInvoiceOptions(
              settings.taxes,
              estimate
            );

            return copyDocument({
              ...copyDocumentOptions,
              saveEditing: true,
              saveCopy: true,
              dispatchId: v4(),
              entityType: EntityTypesEnum.invoice,
              queryParams: {
                [ADD_JOB_QUERY_PARAM]: jobId,
                [ADD_CLIENT_QUERY_PARAM]: clientId,
              },
            });
          }),
          catchError((error) => of(actionError(error)))
        );
      })
    );
  }
}
